import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Menu, X, Heart, User, Settings, LogOut, Calendar, BarChart3, Building, Shield } from 'lucide-react'
// import LanguageSwitcher from './LanguageSwitcher' // Disabled - Hebrew only
import UserMenu from './UserMenu'
import SkipLink from './SkipLink'
import NotificationDropdown from './NotificationDropdown'
import { useAuth } from '../contexts/AuthContext'

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { isAuthenticated, user, logout, hasRole } = useAuth()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navigation = [
    { name: t('navigation.home'), href: '/', current: location.pathname === '/' },
    { name: t('navigation.services'), href: '/services', current: location.pathname === '/services' },
    { name: t('navigation.adoption'), href: '/adoption', current: location.pathname === '/adoption' },
    { name: t('navigation.articles'), href: '/articles', current: location.pathname === '/articles' },
    { name: t('navigation.about'), href: '/about', current: location.pathname === '/about' },
    { name: t('navigation.contact'), href: '/contact', current: location.pathname === '/contact' },
  ]

  const getMobileUserMenuItems = () => {
    if (!isAuthenticated || !user) return []
    
    const items = []
    
    // Common items for all users
    items.push({
      label: t('userMenu.profile'),
      href: '/profile?edit=true',
      icon: User
    })
    
    // Role-specific items
    if (hasRole('consumer')) {
      items.push(
        { label: t('userMenu.favorites'), href: '/favorites', icon: Heart },
        { label: t('userMenu.bookings'), href: '/bookings', icon: Calendar }
      )
    }
    
    if (hasRole('business')) {
      items.push(
        { label: t('userMenu.businessDashboard'), href: '/business-dashboard', icon: BarChart3 },
        { label: t('userMenu.businessProfile'), href: '/business-profile?edit=true', icon: Building },
        { label: t('userMenu.listings'), href: '/business-listings', icon: Building }
      )
    }
    
    if (hasRole('admin')) {
      items.push({ label: t('userMenu.adminDashboard'), href: '/admin-dashboard', icon: Shield })
    }
    
    // Settings for all users
    items.push({ label: t('userMenu.settings'), href: '/settings', icon: Settings })
    
    return items
  }

  const handleMobileLogout = async () => {
    try {
      await logout()
      navigate('/')
      setIsMenuOpen(false)
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  return (
    <>
      <SkipLink href="#main-content">
        {t('navigation.skipToMain')}
      </SkipLink>
      <header className={`bg-white shadow-sm border-b border-gray-200 transition-all duration-300 ${
        isScrolled ? 'shadow-lg' : 'shadow-sm'
      }`} role="banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center group">
              <Heart className="h-6 w-6 sm:h-8 sm:w-8 text-primary-600 group-hover:animate-pulse transition-all duration-300" />
              <span className="mr-2 text-lg sm:text-xl font-bold text-gray-900 group-hover:text-primary-600 transition-colors duration-300">MyPet</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8 space-x-reverse" role="navigation" aria-label={t('navigation.mainAriaLabel')}>
            {navigation.map((item, index) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-all duration-300 hover:scale-105 ${
                  item.current
                    ? 'text-primary-600 bg-primary-50'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                }`}
                style={{ animationDelay: `${index * 100}ms` }}
                aria-current={item.current ? 'page' : undefined}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-2 lg:space-x-4 space-x-reverse">
            {isAuthenticated ? (
              <>
                {!location.pathname.startsWith('/admin-dashboard') && !location.pathname.startsWith('/admin/') && (
                  <NotificationDropdown />
                )}
                <UserMenu />
              </>
            ) : (
              <div className="flex items-center space-x-2 space-x-reverse">
                <Link
                  to="/login"
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                >
                  {t('navigation.login')}
                </Link>
                <Link
                  to="/business-register"
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
                >
                  {t('navigation.register')}
                </Link>
              </div>
            )}
            {/* <LanguageSwitcher /> */} {/* Disabled - Hebrew only */}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-gray-400 hover:text-gray-500 transition-all duration-300 hover:scale-110"
              aria-label={isMenuOpen ? t('navigation.closeMenu') : t('navigation.openMenu')}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
            >
              {isMenuOpen ? (
                <X className="h-6 w-6 animate-spin" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden animate-slide-in-left" id="mobile-menu" role="navigation" aria-label={t('navigation.mobileAriaLabel')}>
            <div className="px-2 pt-2 pb-3 space-y-1 border-t border-gray-200">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-3 py-2 text-base font-medium rounded-md transition-colors ${
                    item.current
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <div className="pt-4 border-t border-gray-200">
                {/* <div className="px-3 py-2">
                  <LanguageSwitcher />
                </div> */} {/* Disabled - Hebrew only */}
                {isAuthenticated ? (
                  <div className="space-y-1">
                    {getMobileUserMenuItems().map((item, index) => {
                      const Icon = item.icon
                      return (
                        <Link
                          key={index}
                          to={item.href}
                          className="flex items-center gap-3 px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <Icon className="h-5 w-5" />
                          {item.label}
                        </Link>
                      )
                    })}
                    <button
                      onClick={handleMobileLogout}
                      className="w-full flex items-center gap-3 px-3 py-2 text-base font-medium text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    >
                      <LogOut className="h-5 w-5" />
                      {t('userMenu.logout')}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <Link
                      to="/login"
                      className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t('navigation.login')}
                    </Link>
                    <Link
                      to="/business-register"
                      className="block px-3 py-2 text-base font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors text-center"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t('navigation.register')}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
    </>
  )
}
